import React from 'react';
import { Link } from 'react-router-dom';
import { DateTime } from "luxon";

import Authenticated from 'Components/Layouts/Authenticated';
import { Loading, Alert } from 'Components/Partials';
import { PrimaryButton, DangerButton, SecondaryButton } from 'Components/Button';
import { RenderWYSIWYG } from 'Components/Form';
import { ModalTrigger } from 'Components/Modal';
import { DisplayTimeZone } from 'Components/Settings';

import AddToCalendarModal from './AddToCalendarModal';

import AgendaApi from 'Services/Api/Agenda/Agenda';
import { Toast } from 'Services';
import { translation } from 'Services/TranslationHelpers';
import { translationFromJson } from 'Services/TranslationHelpers2';
import { resourceDateIsBeforeNow, resourceDateIsAfterNow, dateToString } from 'Services/DateHelpers';
import PageHeader from "Components/Utilities/PageHeader";
import { asset } from "Services/BaseHelpers";
import { getRecordValue } from "Services/CmsHelpers";
import CmsElement from "Components/Utilities/CmsElement";
import RecordApi from "Services/Api/Cms/Elements/Record";

export default class AgendaIndex extends CmsElement {
    cmsElementIdentifier = 'itinerary';

    days = [
        'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE', 'TEN', 'ELEVEN', 'TWELVE',
    ];

    /**
     * @var state
     */
    state = {
        loading: true,
        alert: null,
        agendas: null,
        dates: null,
        active_date: null,
        refresh_time: null,
        working: true,
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = async () => {
        await this.fetchElement();
        await this.fetchRecords();
        await this.fetchTags();
        await this.getAgenda();

        RecordApi.on('updated', this.fetchRecords);

        // Rerender every 60 seconds in order to keep the action buttons in sync.
        this.refreshInterval = setInterval(() => this.setState({ refresh_time: Date.now() }), 60000);
    }

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount = () => {
        clearInterval(this.refreshInterval);
    };

    /**
     * @method getAgenda
     */
    getAgenda = async () => {
        this.setState({
            loading: true
        });

        const request = await AgendaApi.get(null);

        if (request.success) {
            // Get all dates.
            const dates = [...new Set(
                request.data.data.map(agenda => agenda.start_date_time.date_timezone)
            )];

            return this.setState({
                loading: false,
                agendas: request.data.data,
                dates,
                active_date: this.state.active_date ?? dates[0]
            });

            // TODO scroll back to where they were on the page.
        }

        Toast.error();
    };

    /**
     * @method addToAgenda
     * @param {string} agenda_id
     */
    addToAgenda = async (agenda_id) => {
        this.setState({
            loading: true,
            alert: null
        });

        const response = await AgendaApi.post(null, { agenda_id });

        if (response.success) {
            this.setState({
                alert: {
                    type: 'success',
                    message: response.data.message
                }
            });

            return this.getAgenda();
        }

        this.setState({
            alert: {
                type: 'error',
                ...response
            },
            loading: false
        }, () => {
            document.getElementById('alert').scrollIntoView();
        });
    }

    /**
     * @method removeFromAgenda
     * @param {string} agenda_id
     */
    removeFromAgenda = async (agenda_id) => {
        this.setState({
            loading: true,
            alert: null
        });

        const response = await AgendaApi.delete(agenda_id);

        this.getAgenda();
    }

    /**
     * @method showAddToAgendaButton
     * @param {object} agenda
     * @return {boolean}
     */
    showAddToAgendaButton = (agenda) => {
        if (agenda.show_add_to_agenda_button === true) {
            // If dates are null then show.
            if (agenda.show_add_to_agenda_button_start_date_time === null
                && agenda.show_add_to_agenda_button_end_date_time === null) {

                return true;
            }

            if (agenda.show_add_to_agenda_button_start_date_time === null) {
                return resourceDateIsAfterNow(agenda.show_add_to_agenda_button_end_date_time);
            }

            if (agenda.show_add_to_agenda_button_end_date_time === null) {
                return resourceDateIsBeforeNow(agenda.show_add_to_agenda_button_start_date_time);
            }

            return (resourceDateIsBeforeNow(agenda.show_add_to_agenda_button_start_date_time)
                && resourceDateIsAfterNow(agenda.show_add_to_agenda_button_end_date_time));
        }

        return false;
    }

    /**
     * @method showJoinLink
     * @param {object} agenda
     * @return {boolean}
     */
    showJoinLink = (agenda) => {
        if (agenda.join_link) {
            // If dates are null then show.
            if (agenda.join_link_display_start_date_time === null
                && agenda.join_link_display_end_date_time === null) {

                return true;
            }

            if (agenda.join_link_display_start_date_time === null) {
                return resourceDateIsAfterNow(agenda.join_link_display_end_date_time);
            }

            if (agenda.join_link_display_end_date_time === null) {
                return resourceDateIsBeforeNow(agenda.join_link_display_start_date_time);
            }

            return (resourceDateIsBeforeNow(agenda.join_link_display_start_date_time)
                && resourceDateIsAfterNow(agenda.join_link_display_end_date_time));
        }

        return false;
    }

    /**
     * @method render
     * @return {*}
     */
    render() {
        const { loading, alert, agendas, working, records } = this.state;
        const content = {};

        records && records.data.forEach(item => {
            content[getRecordValue(item, 'title')] = getRecordValue(item, 'content');
        })

        return (
            <Authenticated>
                <PageHeader
                    imgSrc="headers/the-itinerary.jpg"
                />

                { working && loading && (<Loading/>) }

                { !working && content && (
                    <>
                        <div className="max-w-4xl mx-auto py-10 px-5 md:px-10">
                            <div className="flex justify-center pb-4">
                                <img
                                    src={ asset('images/mini-logo.png') }
                                    alt="Logo"
                                    className="block w-20 align-self-center"
                                />
                            </div>

                            <div className="text-center">
                                <RenderWYSIWYG
                                    className="text-black"
                                    content={ content['Welcome Text'] ?? '' }
                                />
                            </div>
                        </div>
                    </>
                ) }

                {!loading && (
                    <div className="p-6 md:p-12">
                        {this.renderDates()}
                        {this.renderAgendas()}
                    </div>
                )}
            </Authenticated>
        );
    }

    /**
     * @method renderDates
     * @return {*}
     */
    renderDates() {
        const { dates, active_date } = this.state;

        return (
            <div className="flex flex-col md:flex-row mx-auto justify-center w-full md:divide-x divide-black border-t border-black border-x">
                { dates && dates.map((date, i) => {
                    return (
                        <button
                            key={ i }
                            onClick={ () => this.setState({ active_date: date }) }
                            className={ `
                                w-full py-4 px-8 uppercase font-bold
                                ${ active_date === date ?
                                    'bg-black text-white' :
                                    'bg-app-yellow text-black transition duration-200 hover:bg-black hover:text-white'
                                }
                            ` }
                        >
                            DAY {this.days[i]}
                        </button>
                    )
                }) }
            </div>
        );
    }

    /**
     * @method renderAgendas
     * @return {*}
     */
    renderAgendas() {
        const { dates, agendas, active_date } = this.state;
        const i = dates.indexOf(active_date);
        const agendaItems = agendas.filter(agenda => agenda.start_date_time.date_timezone === active_date);

        return (
            <div className="bg-black p-6 md:p-12 flex flex-col md:flex-row">
                <div className="w-full md:w-1/2">
                    <p className="uppercase font-bold text-white text-3xl mb-12">
                        {DateTime.fromFormat(active_date, 'dd/MM/yyyy').toFormat('EEEE dd MMMM')}.
                    </p>

                    <div className="space-y-6 relative">
                        <div className="absolute inset-y-4 inset-x-0 flex text-white">
                            <div className="w-1/4 md:w-1/5 xl:w-1/12 font-bold"></div>

                            <div className="w-1/12 flex justify-center items-center relative">
                                <div className="w-px bg-white h-full absolute left-1/2"></div>
                            </div>
                        </div>

                        {agendaItems.map((item, key) => (
                            <div className="relative">
                                {key === 0 && (
                                    <div className="w-full h-1/2 bg-black absolute top-0 inset-x-0 z-0"></div>
                                )}

                                <div className="flex items-center text-white z-10 relative" key={item.id}>
                                    <div className="w-1/4 md:w-1/5 xl:w-1/12 font-bold">
                                        {item.time_text ?? DateTime.fromISO(item.start_date_time?.iso_string).toFormat('HH:mm')}
                                    </div>

                                    <div className="w-1/12">
                                        <div className="flex justify-center items-center relative">
                                            <div className="rounded-full bg-white w-3 h-3 z-10" />
                                        </div>
                                    </div>

                                    <div className="flex-1 font-bold">
                                        {translationFromJson(item.title)}

                                        {item.content && (
                                            <RenderWYSIWYG
                                                className={"text-white text-sm font-medium"}
                                                content={ translationFromJson(item.content) }
                                            />
                                        )}
                                    </div>
                                </div>

                                {key + 1 === agendaItems.length && (
                                    <div className="w-full h-1/2 bg-black absolute bottom-0 inset-x-0 z-0"></div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="w-full md:w-1/2 mt-6 md:mt-0">
                    <img src={asset(`images/${this.days[i]}.jpg`)} />
                </div>
            </div>
        )

        // Render desktop and mobile layout.
        return (
            <>
                <div className="hidden md:inline overflow-x-auto">
                    <table className="table-auto mx-auto">
                        <thead>
                        <tr>
                            <th className="px-4 py-2 flex">
                                { translation('agenda', 'time') } ({ <DisplayTimeZone containerClassName="w-72"/> })
                            </th>
                            <th className="px-4 py-2">
                                { translation('agenda', 'title') }
                            </th>
                            <th className="px-4 py-2">
                                { translation('agenda', 'description') }
                            </th>
                            <th className="px-4 py-2">
                                { translation('agenda', 'presenters') }
                            </th>
                            <th className="px-4 py-2">
                                { translation('agenda', 'actions') }
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        { agendas.filter(agenda => agenda.start_date_time.date_timezone === active_date)
                            .map((agenda, i) => {
                                return (
                                    <tr key={ i }>
                                        <td className="px-4 py-2 border">
                                            { agenda.start_date_time.time_timezone }
                                        </td>
                                        <td className="px-4 py-2 border">
                                            { translationFromJson(agenda.title) }
                                        </td>
                                        <td className="px-4 py-2 border">
                                            <RenderWYSIWYG content={ translationFromJson(agenda.content) }/>
                                        </td>
                                        <td className="px-4 py-2 border">
                                            <RenderWYSIWYG content={ translationFromJson(agenda.presenters) }/>
                                        </td>
                                        <td className="px-4 py-2 border">
                                            { this.renderAgendaActions(agenda) }
                                        </td>

                                        { this.showAddToAgendaButton(agenda) &&
                                            <td className="px-4 py-2 border">
                                                { this.renderSpacesColumn(agenda) }
                                            </td>
                                        }
                                    </tr>
                                );
                            }) }
                        </tbody>
                    </table>
                </div>

                <div className="inline md:hidden">
                    { agendas.filter(agenda => agenda.start_date_time.date_timezone === active_date)
                        .map((agenda, i) => {
                            return (
                                <div className="mb-4" key={ i }>
                                    <div className="flex border-b-2">
                                        <div
                                            className="px-4 py-2 w-1/2 bg-light-blue-800 text-white flex items-center justify-center">
                                            { translation('agenda', 'time') } ({ <DisplayTimeZone
                                            containerClassName="w-72"/> })
                                        </div>
                                        <div className="px-4 py-2 w-1/2 bg-gray-300 text-black">
                                            { agenda.start_date_time.time_timezone }
                                        </div>
                                    </div>
                                    <div className="flex border-b-2">
                                        <div
                                            className="px-4 py-2 w-1/2 bg-light-blue-800 text-white flex items-center justify-center">
                                            { translation('agenda', 'title') }
                                        </div>
                                        <div className="px-4 py-2 w-1/2 bg-gray-300 text-black">
                                            { translationFromJson(agenda.title) }
                                        </div>
                                    </div>
                                    <div className="flex border-b-2">
                                        <div
                                            className="px-4 py-2 w-1/2 bg-light-blue-800 text-white flex items-center justify-center">
                                            { translation('agenda', 'description') }
                                        </div>
                                        <div className="px-4 py-2 w-1/2 bg-gray-300 text-black">
                                            <RenderWYSIWYG content={ translationFromJson(agenda.content) }/>
                                        </div>
                                    </div>
                                    <div className="flex border-b-2">
                                        <div
                                            className="px-4 py-2 w-1/2 bg-light-blue-800 text-white flex items-center justify-center">
                                            { translation('agenda', 'presenters') }
                                        </div>
                                        <div className="px-4 py-2 w-1/2 bg-gray-300 text-black">
                                            <RenderWYSIWYG content={ translationFromJson(agenda.presenters) }/>
                                        </div>
                                    </div>
                                    <div className="flex border-b-2">
                                        <div className="px-4 py-2 w-full bg-gray-300 text-black">
                                            { this.renderAgendaActions(agenda) }
                                        </div>
                                    </div>
                                    <div className="flex border-b-2">
                                        { this.showAddToAgendaButton(agenda) &&
                                            <div className="px-4 py-2 w-full bg-gray-300 text-black">
                                                { this.renderSpacesColumn(agenda) }
                                            </div>
                                        }
                                    </div>
                                </div>
                            );
                        }) }
                </div>
            </>
        );
    }

    /**
     * @method renderAgendaActions
     * @param {object} agenda
     * @return {*}
     */
    renderAgendaActions(agenda) {
        return (
            <div className="flex flex-col">
                { this.showAddToAgendaButton(agenda) &&
                    this.renderAddToAgendaButton(agenda)
                }

                { (agenda.show_add_to_calendar_button &&
                        (agenda.show_add_to_calendar_button_only_if_in_agenda === false || agenda.in_my_agenda)) &&
                    this.renderAddToCalendarButton(agenda)
                }

                { (this.showJoinLink(agenda) &&
                        (agenda.join_link_display_only_if_in_agenda === false || agenda.in_my_agenda)) &&
                    this.renderJoinLinkButton(agenda)
                }
            </div>
        );
    }

    /**
     * @method renderAddToAgendaButton
     * @param {object} agenda
     * @return {*}
     */
    renderAddToAgendaButton(agenda) {
        let text = null;
        let onClickMethod = null;
        let Component = null;

        if (agenda.in_my_agenda) {
            text = translation('agenda', 'remove_from_your_agenda');
            onClickMethod = 'removeFromAgenda';
            Component = DangerButton;
        } else if (agenda.in_my_waiting_list) {
            text = translation('agenda', 'remove_from_waitlist');
            onClickMethod = 'removeFromAgenda';
            Component = DangerButton;
        } else {
            if (!agenda.spaces || agenda.available_spaces > 0) {
                text = translation('agenda', 'add_to_your_agenda');
                onClickMethod = 'addToAgenda';
                Component = PrimaryButton;
            } else if (agenda.spaces && agenda.available_spaces < 1 && agenda.waiting_list) {
                text = translation('agenda', 'add_to_waitlist');
                onClickMethod = 'addToAgenda';
                Component = PrimaryButton;
            } else {
                text = translation('agenda', 'session_is_fully_booked_short');
                onClickMethod = null;
                Component = DangerButton;
            }
        }

        return (
            <div className="my-2">
                <Component
                    text={ text }
                    onClick={ () => onClickMethod ? this[onClickMethod](agenda.id) : null }/>
            </div>
        );
    }

    /**
     * @method renderAddToCalendarButton
     * @param {object} agenda
     * @return {*}
     */
    renderAddToCalendarButton(agenda) {
        return (
            <div className="my-2">
                <ModalTrigger
                    component={ AddToCalendarModal }
                    props={ {
                        agenda
                    } }
                >
                    <SecondaryButton text={ translation('agenda', 'add_to_calendar') }/>
                </ModalTrigger>
            </div>
        );
    }

    /**
     * @method renderJoinLinkButton
     * @param {object} agenda
     * @return {*}
     */
    renderJoinLinkButton(agenda) {
        return (
            <a href={ agenda.join_link } target="_blank" className="my-2">
                <PrimaryButton text={ translation('agenda', 'join') }/>
            </a>
        );
    }

    /**
     * @method renderSpacesColumn
     * @param {object} agenda
     * @return {*}
     */
    renderSpacesColumn(agenda) {
        return (
            <span className="font-bold">
                { agenda.spaces &&
                    <>
                        { agenda.available_spaces === 1 ?
                            translation('agenda', '1_space_available') :
                            agenda.available_spaces + ' ' + translation('agenda', 'spaces_available') }
                    </>
                }

                { !agenda.spaces &&
                    <>
                        { translation('agenda', 'no_limit') }
                    </>
                }
            </span>
        );
    }
}
